import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLoading } from '../context/LoadingContext';
import { saveDefaultLocale, saveDynamicWordings, savePageWordings } from '../internal/actions/appActions';
import { LOCALE_ENDPOINT } from '../internal/constants/api';
import useApi, { Methods } from '../internal/hooks/useApi';
import { WordingsType } from '../internal/mocks/wordings';
import { selectLocale } from '../internal/selectors/localeSelectors';
import { selectDefaultLanguage } from '../internal/selectors/remoteConfigSelectors';

function withConfig<T>(WrappedComponent: React.ComponentType<T>) {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithConfig = (props: T) => {
    const { i18n } = useTranslation();
    const dispatch = useDispatch();
    const { setDataIsLoaded, setLoading } = useLoading();
    const locale = useSelector(selectLocale);
    const defaultLocale = useSelector(selectDefaultLanguage);
    // const { request: postLang } = useApi(Methods.POST, 'user/lang');

    const { request: fetchLocales, response: localeResp } = useApi<{
      translations: Record<string, string | WordingsType>;
    }>(Methods.GET, LOCALE_ENDPOINT);

    useEffect(() => {
      if (locale || defaultLocale) {
        setLoading(true);
        setDataIsLoaded(false);
        fetchLocales({
          params: {
            lang: locale || defaultLocale,
          },
        });
      }
    }, [fetchLocales, locale, defaultLocale]);

    useEffect(() => {
      if (!locale && defaultLocale) {
        dispatch(saveDefaultLocale(defaultLocale));
        i18n.changeLanguage(defaultLocale);
      }
    }, [locale, defaultLocale]);

    useEffect(() => {
      if (localeResp?.success && localeResp.data) {
        i18n.addResourceBundle(
          locale || defaultLocale || navigator.language.split('-')[0],
          'translations',
          localeResp.data.translations,
          true,
          true
        );
        dispatch(savePageWordings(localeResp.data.translations?.dynamicWording as WordingsType));
        dispatch(
          saveDynamicWordings({
            ...(localeResp.data.translations?.dynamicStrings as Record<string, string>),
            ...(localeResp.data.translations?.contactHookInfo as Record<string, string>),
          })
        );

        setDataIsLoaded(true);
      }
    }, [localeResp, i18n, locale, defaultLocale]);

    return <WrappedComponent {...props} />;
  };

  ComponentWithConfig.displayName = `withConfig(${displayName})`;

  return ComponentWithConfig;
}

export default withConfig;
